import React, { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, TrendingUp, DollarSign, Trophy, Users, Calendar, Icon } from "lucide-react";
import { horseHead } from '@lucide/lab';
import { getApiUrl } from "../config/api";
import { useNotification } from "../contexts/NotificationContext";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function Analytics() {
  const { showError } = useNotification();
  const [analyticsData, setAnalyticsData] = useState({
    financial: {
      totalEarnings: 0,
      totalExpenses: 0,
      netProfit: 0,
      horseCareCosts: 0,
      competitionCosts: 0,
      membershipCosts: 0
    },
    performance: {
      totalPoints: 0,
      totalShows: 0,
      averagePointsPerShow: 0,
      topPerformingHorses: [],
      topPerformingShows: []
    },
    horses: {
      totalHorses: 0,
      averageCostPerHorse: 0,
      horseBreakdown: []
    },
    yearly: {
      currentYear: new Date().getFullYear(),
      monthlyData: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [familyContacts, setFamilyContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState('all');
  const [userSubscription, setUserSubscription] = useState(null);

  // Helper function to check if user has basic subscription
  const isBasicSubscription = () => {
    return userSubscription?.tier?.name === 'basic' || userSubscription?.tier_name === 'basic';
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedYear, selectedContactId]);

  useEffect(() => {
    // Load current user, family contacts, and subscription for filter
    const loadUserAndContacts = async () => {
      try {
        // Load current user
        const userRes = await axios.get(getApiUrl('/api/profile'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setCurrentUser(userRes.data);

        // Load family members
        const contactsRes = await axios.get(getApiUrl('/api/settings/family-members'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setFamilyContacts(contactsRes.data || []);

        // Load user subscription
        const subscriptionRes = await axios.get(getApiUrl('/api/subscriptions/current'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setUserSubscription(subscriptionRes.data.subscription);
      } catch (e) {
        // Silent fail for user/contacts/subscription load
      }
    };
    loadUserAndContacts();
  }, []);

  const fetchAnalyticsData = async () => {
    const startTime = performance.now();
    
    try {
      setLoading(true);
      
      // Fetch data from multiple endpoints with error handling
      const mainDataStart = performance.now();
      
      // Add individual timeouts to each API call
      const createTimeoutPromise = (ms) => new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Request timeout after ${ms}ms`)), ms)
      );
      
      const [results, entries, memberships, horses, supplies] = await Promise.allSettled([
        Promise.race([
          axios.get(getApiUrl("/api/results"), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }),
          createTimeoutPromise(10000) // 10 second timeout
        ]),
        Promise.race([
          axios.get(getApiUrl("/api/entries"), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }),
          createTimeoutPromise(10000) // 10 second timeout
        ]),
        Promise.race([
          axios.get(getApiUrl("/api/associations/memberships"), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }),
          createTimeoutPromise(10000) // 10 second timeout
        ]),
        Promise.race([
          axios.get(getApiUrl("/api/horses"), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }),
          createTimeoutPromise(10000) // 10 second timeout
        ]),
        Promise.race([
          axios.get(getApiUrl("/api/supplies/orders"), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }),
          createTimeoutPromise(10000)
        ])
      ]);

      const mainDataTime = performance.now() - mainDataStart;

      // Extract data with fallbacks for failed requests
      let resultsData = results.status === 'fulfilled' ? results.value.data : [];
      let entriesData = entries.status === 'fulfilled' ? entries.value.data : [];
      let membershipsData = memberships.status === 'fulfilled' ? memberships.value.data : [];
      let horsesData = horses.status === 'fulfilled' ? horses.value.data : [];
      let suppliesOrdersData = supplies.status === 'fulfilled' ? supplies.value.data : [];

      // Ensure all data is arrays
      if (!Array.isArray(resultsData)) {
        resultsData = [];
      }
      if (!Array.isArray(entriesData)) {
        entriesData = [];
      }
      if (!Array.isArray(membershipsData)) {
        membershipsData = [];
      }
      if (!Array.isArray(horsesData)) {
        horsesData = [];
      }

      // Filter by selected family contact or current user
      if (selectedContactId && selectedContactId !== 'all') {
        if (selectedContactId === 'current_user' && currentUser) {
          // Filter for current user's data (records without family_member_id)
          const matchesCurrentUser = (record) => {
            return !record.family_member_id || record.family_member_id === null;
          };
          resultsData = Array.isArray(resultsData) ? resultsData.filter(matchesCurrentUser) : [];
          entriesData = Array.isArray(entriesData) ? entriesData.filter(matchesCurrentUser) : [];
          membershipsData = Array.isArray(membershipsData) ? membershipsData.filter(matchesCurrentUser) : [];
          horsesData = Array.isArray(horsesData) ? horsesData.filter(matchesCurrentUser) : [];
        } else {
          // Filter for specific family member
          const contact = familyContacts.find(c => String(c.id) === String(selectedContactId));
          if (contact) {
            const matchesContact = (record) => {
              // Check if record belongs to this family member
              const recordFamilyMemberId = record.family_member_id;
              const contactId = String(contact.id);
              const matches = recordFamilyMemberId && String(recordFamilyMemberId) === contactId;
              return matches;
            };
            
            resultsData = Array.isArray(resultsData) ? resultsData.filter(matchesContact) : [];
            entriesData = Array.isArray(entriesData) ? entriesData.filter(matchesContact) : [];
            membershipsData = Array.isArray(membershipsData) ? membershipsData.filter(matchesContact) : [];
            horsesData = Array.isArray(horsesData) ? horsesData.filter(matchesContact) : [];
            
          } else {
            console.warn('❌ ANALYTICS: Contact not found for ID:', selectedContactId);
          }
        }
      }

      // Fetch horse care costs for each horse (after filtering)
      const horseCareStart = performance.now();
      const horseCareCosts = await fetchHorseCareCosts(horsesData);
      const horseCareTime = performance.now() - horseCareStart;

      // Process and calculate analytics
      const processStart = performance.now();
      const processedData = processAnalyticsData(
        resultsData,
        entriesData,
        membershipsData,
        horsesData,
        horseCareCosts,
        suppliesOrdersData
      );
      //const processTime = performance.now() - processStart;
      //const totalTime = performance.now() - startTime;

      setAnalyticsData(processedData);
    } catch (error) {
      showError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchHorseCareCosts = async (horses) => {
    try {
      const horseCareData = {};
      
      // Fetch care costs for each horse
      for (let i = 0; i < horses.length; i++) {
        const horse = horses[i];
        const horseStart = performance.now();
        
        const [farrier, dental, deworming, medical, breeding] = await Promise.all([
          axios.get(getApiUrl(`/api/horses/${horse.id}/farrier`), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }).catch(() => ({ data: [] })),
          axios.get(getApiUrl(`/api/horses/${horse.id}/dentalcare`), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }).catch(() => ({ data: [] })),
          axios.get(getApiUrl(`/api/horses/${horse.id}/deworming`), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }).catch(() => ({ data: [] })),
          axios.get(getApiUrl(`/api/horses/${horse.id}/medicalhistory`), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }).catch(() => ({ data: [] })),
          axios.get(getApiUrl(`/api/horses/${horse.id}/breedings`), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }).catch(() => ({ data: [] }))
        ]);

        const horseTime = performance.now() - horseStart;

        const totalCost = [
          ...farrier.data,
          ...dental.data,
          ...deworming.data,
          ...medical.data,
          ...breeding.data
        ].reduce((sum, record) => {
          const amount = record.Amount || record.amount || 0;
          return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
        }, 0);

        horseCareData[horse.id] = {
          name: horse.name,
          totalCost,
          breakdown: {
            farrier: farrier.data.reduce((sum, r) => {
              const amount = r.Amount || r.amount || 0;
              return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
            }, 0),
            dental: dental.data.reduce((sum, r) => {
              const amount = r.Amount || r.amount || 0;
              return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
            }, 0),
            deworming: deworming.data.reduce((sum, r) => {
              const amount = r.Amount || r.amount || 0;
              return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
            }, 0),
            medical: medical.data.reduce((sum, r) => {
              const amount = r.Amount || r.amount || 0;
              return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
            }, 0),
            breeding: breeding.data.reduce((sum, r) => {
              const amount = r.Amount || r.amount || 0;
              return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
            }, 0)
          }
        };
      }
      
      return horseCareData;
    } catch (error) {
      console.error("❌ ANALYTICS: Error fetching horse care costs:", error);
      return {};
    }
  };

  const processAnalyticsData = (results, entries, memberships, horses, horseCareData, suppliesOrders) => {
    const currentYear = selectedYear;
    
    // Filter data by selected year
    const yearResults = results.filter(r => {
      // Try different date fields that might exist in the results
      const showDate = r.show_date || r.show_start_date || r.first_event_date;
      
      // Also check if there's a date in the result_data JSON
      const resultDataDate = r.result_data?.ShowDate || r.result_data?.date;
      const finalDate = showDate || resultDataDate;
      
      if (!finalDate) return false;
      
      const resultYear = new Date(finalDate).getFullYear();
      return resultYear === currentYear;
    });



    // Calculate financial metrics
    const totalEarnings = yearResults.reduce((sum, r) => {
      // Sum earnings from all events in the result_data JSON
      const eventsEarnings = r.result_data?.events?.reduce((eventSum, event) => {
        // Check if earnings are directly on the event
        if (event.earnings) {
          return eventSum + (parseFloat(event.earnings) || 0);
        }
        
        // Check if earnings are in the draws/contestants
        const drawsEarnings = event.draws?.reduce((drawSum, draw) => {
          const contestantsEarnings = draw.contestants?.reduce((contestantSum, contestant) => {
            return contestantSum + (parseFloat(contestant.earnings) || 0);
          }, 0) || 0;
          return drawSum + contestantsEarnings;
        }, 0) || 0;
        
        return eventSum + drawsEarnings;
      }, 0) || 0;
      

      
      return sum + eventsEarnings;
    }, 0);
    
    const totalPoints = yearResults.reduce((sum, r) => {
      // Sum points from all events in the result_data JSON
      const eventsPoints = r.result_data?.events?.reduce((eventSum, event) => {
        // Check if points are directly on the event
        if (event.points) {
          return eventSum + (parseInt(event.points) || 0);
        }
        
        // Check if points are in the draws/contestants
        const drawsPoints = event.draws?.reduce((drawSum, draw) => {
          const contestantsPoints = draw.contestants?.reduce((contestantSum, contestant) => {
            return contestantSum + (parseInt(contestant.points) || 0);
          }, 0) || 0;
          return drawSum + contestantsPoints;
        }, 0) || 0;
        
        return eventSum + drawsPoints;
      }, 0) || 0;
      return sum + eventsPoints;
    }, 0);


    
    // Filter entries that were active during the selected year
    const yearEntries = entries.filter(e => {
      // Try different date fields that might exist in the entries
      const showDate = e.show_date || e.show_start_date || e.first_event_date;
      if (!showDate) return false;
      
      const entryYear = new Date(showDate).getFullYear();
      return entryYear === currentYear;
    });
    
    
    // Filter memberships that were active during the selected year
    // Include memberships that were active at any point during the selected year
    const yearMemberships = memberships.filter(m => {
      // Try different date fields for memberships
      const startDate = m.start_date || m.created_at || m.date;
      const endDate = m.expiration_date;
      
      if (!startDate) {
        return false;
      }
      
      const membershipStartYear = new Date(startDate).getFullYear();
      const membershipEndYear = endDate ? new Date(endDate).getFullYear() : membershipStartYear;
      
      // Include if membership was active during the selected year
      // (started before or during the year AND ended after or during the year)
      const isActiveInYear = membershipStartYear <= currentYear && membershipEndYear >= currentYear;
      
      return isActiveInYear;
    });
    

    // Calculate expenses
    const entryCosts = yearEntries.reduce((sum, e) => {
      // Access events and fees from the nested entry_data structure
      const events = e.entry_data?.events || [];
      const fees = e.entry_data?.fees || [];
      
      // Calculate events total including carry-overs
      const eventsTotal = events.reduce((eventSum, event) => {
        // Try multiple possible field names for the event amount
        const baseAmount = parseFloat(event.EntryAmount ?? event.entryAmount ?? event.amount ?? event.Amount ?? 0) || 0;
        const carryOversTotal = Array.isArray(event.carryOvers) 
          ? event.carryOvers.reduce((coSum, co) => {
              const coAmount = parseFloat(co.CoAmount ?? co.coAmount ?? co.EntryAmount ?? co.entryAmount ?? co.Amount ?? co.amount);
              return coSum + (isNaN(coAmount) ? 0 : coAmount);
            }, 0)
          : 0;
        return eventSum + baseAmount + carryOversTotal;
      }, 0);
      
      // Calculate fees total (quantity * amount)
      const feesTotal = fees.reduce((feeSum, fee) => {
        const quantity = parseInt(fee.quantity) || 1;
        const feeAmount = parseFloat(fee.amount) || 0;
        return feeSum + (quantity * feeAmount);
      }, 0);
      
      return sum + eventsTotal + feesTotal;
    }, 0);
    

    
    const membershipCosts = yearMemberships.reduce((sum, m) => {
      const amount = m.amount || 0;
      // Convert amounts to numbers (now numeric from database)
      const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
      
      return sum + numericAmount;
    }, 0);


    
    // Calculate horse care costs from the fetched data
    const horseCareCosts = Object.values(horseCareData).reduce((sum, horse) => sum + horse.totalCost, 0);
    
    // Supplies expenses (header-level grand_total or sum of lines)
    const yearSupplies = Array.isArray(suppliesOrders) ? suppliesOrders.filter(o => {
      if (!o.order_date) return false;
      return new Date(o.order_date).getFullYear() === currentYear;
    }) : [];
    const suppliesCosts = yearSupplies.reduce((sum, o) => {
      const amount = o.grand_total ?? (o.subtotal + o.tax_total + o.shipping + o.other_total) ?? 0;
      const numeric = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
      return sum + numeric;
    }, 0);

    const totalExpenses = entryCosts + membershipCosts + horseCareCosts + suppliesCosts;
    const netProfit = totalEarnings - totalExpenses;

    // Calculate performance metrics
    // Total results count (all results, not filtered by year)
    const totalResults = results.length;
    // Year-filtered results count
    const totalShows = yearResults.length;
    const averagePointsPerShow = totalShows > 0 ? totalPoints / totalShows : 0;

    // Calculate horse metrics
    const totalHorses = horses.length;
    const averageCostPerHorse = totalHorses > 0 ? totalExpenses / totalHorses : 0;

    // Get top performing horses (by earnings)
    const horsePerformance = horses.map(horse => {
      // Find results that have this horse either by horse_id OR by horse name in events
      const horseResults = yearResults.filter(r => {
        // First check if the result has this horse by ID
        if (r.horse_id === horse.id) return true;
        
        // Then check if any events in the result have this horse by name
        const hasHorseInEvents = r.result_data?.events?.some(event => {
          // Check if horse name is directly on the event (case insensitive)
          if (event.stockname?.toLowerCase() === horse.name.toLowerCase() || 
              event.stockName?.toLowerCase() === horse.name.toLowerCase()) {
            return true;
          }
          
          // Check if horse name is in the contestants (case insensitive)
          return event.draws?.some(draw => 
            draw.contestants?.some(contestant => 
              contestant.stockname?.toLowerCase() === horse.name.toLowerCase() || 
              contestant.stockName?.toLowerCase() === horse.name.toLowerCase() ||
              contestant.horse_name?.toLowerCase() === horse.name.toLowerCase()
            )
          );
        });
        

        
        return hasHorseInEvents;
      });
      
      const totalEarnings = horseResults.reduce((sum, r) => {
        // Sum earnings from all events in the result_data JSON
        const eventsEarnings = r.result_data?.events?.reduce((eventSum, event) => {
          // Check if earnings are directly on the event
          if (event.earnings) {
            const isHorseEvent = r.horse_id === horse.id || 
                                event.stockname?.toLowerCase() === horse.name.toLowerCase();
            if (isHorseEvent) {
              return eventSum + (parseFloat(event.earnings) || 0);
            }
          }
          
          // Check if earnings are in the draws/contestants
          const drawsEarnings = event.draws?.reduce((drawSum, draw) => {
            const contestantsEarnings = draw.contestants?.reduce((contestantSum, contestant) => {
              // Check if this contestant belongs to this horse
              const isHorseContestant = r.horse_id === horse.id || 
                                       contestant.stockname?.toLowerCase() === horse.name.toLowerCase() ||
                                       contestant.stockName?.toLowerCase() === horse.name.toLowerCase() ||
                                       contestant.horse_name?.toLowerCase() === horse.name.toLowerCase();
              
              if (isHorseContestant) {
                return contestantSum + (parseFloat(contestant.earnings) || 0);
              }
              return contestantSum;
            }, 0) || 0;
            return drawSum + contestantsEarnings;
          }, 0) || 0;
          
          return eventSum + drawsEarnings;
        }, 0) || 0;
        
        return sum + eventsEarnings;
      }, 0);
      
      const totalPoints = horseResults.reduce((sum, r) => {
        // Sum points from all events in the result_data JSON
        const eventsPoints = r.result_data?.events?.reduce((eventSum, event) => {
          // Check if points are directly on the event
          if (event.points) {
            const isHorseEvent = r.horse_id === horse.id || 
                                event.stockname?.toLowerCase() === horse.name.toLowerCase();
            if (isHorseEvent) {
              return eventSum + (parseInt(event.points) || 0);
            }
          }
          
          // Check if points are in the draws/contestants
          const drawsPoints = event.draws?.reduce((drawSum, draw) => {
            const contestantsPoints = draw.contestants?.reduce((contestantSum, contestant) => {
              // Check if this contestant belongs to this horse
              const isHorseContestant = r.horse_id === horse.id || 
                                       contestant.stockname?.toLowerCase() === horse.name.toLowerCase() ||
                                       contestant.stockName?.toLowerCase() === horse.name.toLowerCase() ||
                                       contestant.horse_name?.toLowerCase() === horse.name.toLowerCase();
              
              if (isHorseContestant) {
                return contestantSum + (parseInt(contestant.points) || 0);
              }
              return contestantSum;
            }, 0) || 0;
            return drawSum + contestantsPoints;
          }, 0) || 0;
          
          return eventSum + drawsPoints;
        }, 0) || 0;
        
        return sum + eventsPoints;
      }, 0);
      
      const careCosts = horseCareData[horse.id]?.totalCost || 0;
      
      return {
        id: horse.id,
        name: horse.name,
        earnings: totalEarnings,
        points: totalPoints,
        careCosts,
        netProfit: totalEarnings - careCosts,
        shows: horseResults.length
      };
    }).sort((a, b) => b.earnings - a.earnings).slice(0, 5);

    // Get top performing shows (by earnings)
    const showPerformance = yearResults.reduce((showMap, result) => {
      const showName = result.show_name || result.result_data?.ShowName || 'Unknown Show';
      const showDate = result.show_start_date || result.show_date || result.first_event_date || result.result_data?.ShowDate;
      
      if (!showMap[showName]) {
        showMap[showName] = {
          name: showName,
          date: showDate,
          totalEarnings: 0,
          totalPoints: 0,
          totalCosts: 0,
          eventCount: 0,
          horseCount: new Set()
        };
      }
      
      // Add earnings and points from this result (using same logic as main calculation)
      const eventsEarnings = result.result_data?.events?.reduce((eventSum, event) => {
        // Check if earnings are directly on the event
        if (event.earnings) {
          return eventSum + (parseFloat(event.earnings) || 0);
        }
        
        // Check if earnings are in the draws/contestants
        const drawsEarnings = event.draws?.reduce((drawSum, draw) => {
          const contestantsEarnings = draw.contestants?.reduce((contestantSum, contestant) => {
            return contestantSum + (parseFloat(contestant.earnings) || 0);
          }, 0) || 0;
          return drawSum + contestantsEarnings;
        }, 0) || 0;
        
        return eventSum + drawsEarnings;
      }, 0) || 0;
      
      const eventsPoints = result.result_data?.events?.reduce((eventSum, event) => {
        // Check if points are directly on the event
        if (event.points) {
          return eventSum + (parseInt(event.points) || 0);
        }
        
        // Check if points are in the draws/contestants
        const drawsPoints = event.draws?.reduce((drawSum, draw) => {
          const contestantsPoints = draw.contestants?.reduce((contestantSum, contestant) => {
            return contestantSum + (parseInt(contestant.points) || 0);
          }, 0) || 0;
          return drawSum + contestantsPoints;
        }, 0) || 0;
        
        return eventSum + drawsPoints;
      }, 0) || 0;
      
      showMap[showName].totalEarnings += eventsEarnings;
      showMap[showName].totalPoints += eventsPoints;
      showMap[showName].eventCount += result.result_data?.events?.length || 0;
      
      // Add horses from result
      if (result.horse_id) showMap[showName].horseCount.add(result.horse_id);
      
      // Add horses from contestants in events
      result.result_data?.events?.forEach(event => {
        event.draws?.forEach(draw => {
          draw.contestants?.forEach(contestant => {
            if (contestant.stockname || contestant.stockName || contestant.horse_name) {
              const horseName = contestant.stockname || contestant.stockName || contestant.horse_name;
              showMap[showName].horseCount.add(horseName);
            }
          });
        });
      });
      
      return showMap;
    }, {});

    // Calculate show costs from entries
    yearEntries.forEach(entry => {
      if (entry.show_name && showPerformance[entry.show_name]) {
        const eventsTotal = entry.events?.reduce((eventSum, event) => eventSum + (event.amount || 0), 0) || 0;
        const feesTotal = entry.fees?.reduce((feeSum, fee) => {
          const quantity = parseInt(fee.quantity) || 1;
          const amount = parseFloat(fee.amount) || 0;
          return feeSum + (quantity * amount);
        }, 0) || 0;
        showPerformance[entry.show_name].totalCosts += eventsTotal + feesTotal;
      }
    });

    // Convert to array and calculate net profit
    const topPerformingShows = Object.values(showPerformance)
      .map(show => ({
        ...show,
        horseCount: show.horseCount.size,
        netProfit: show.totalEarnings - show.totalCosts
      }))
      .sort((a, b) => b.totalEarnings - a.totalEarnings)
      .slice(0, 5);

    // Calculate grand total of horse costs
    const grandTotalHorseCosts = Object.values(horseCareData).reduce((sum, horse) => sum + horse.totalCost, 0);

    return {
      financial: {
        totalEarnings,
        totalExpenses,
        netProfit,
        horseCareCosts,
        competitionCosts: entryCosts,
        membershipCosts,
        suppliesCosts
      },
      performance: {
        totalPoints,
        totalResults,
        totalShows,
        averagePointsPerShow,
        topPerformingHorses: horsePerformance,
        topPerformingShows: topPerformingShows
      },
      horses: {
        totalHorses,
        averageCostPerHorse,
        grandTotalHorseCosts, // Add grand total
        horseBreakdown: Object.values(horseCareData).map(horse => ({
          name: horse.name,
          totalCost: horse.totalCost,
          breakdown: horse.breakdown
        }))
      },
      yearly: {
        currentYear,
        monthlyData: [] // Will be enhanced
      }
    };
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-kci-600">Loading analytics...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-black-900 flex items-center">
          <BarChart3 className="mr-2 h-6 w-6 sm:h-8 sm:w-8 text-black" />
          Analytics Dashboard
        </h1>
        <p className="text-kci-600 mt-2 text-sm sm:text-base">
          Comprehensive insights into your performance and financial metrics
        </p>
        
        {/* Filters */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center">
            <label className="text-sm font-medium text-kci-700 mb-2 sm:mb-0 sm:mr-2">Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-kci-300 rounded px-3 py-2 text-sm w-full sm:w-auto"
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <label className="text-sm font-medium text-kci-700 mb-2 sm:mb-0 sm:mr-2">Family:</label>
            <select
              value={selectedContactId}
              onChange={(e) => setSelectedContactId(e.target.value)}
              className="border border-kci-300 rounded px-3 py-2 text-sm w-full sm:w-auto"
            >
              <option value="all">All</option>
              {currentUser && (
                <option value="current_user">{currentUser.name} (You)</option>
              )}
              {familyContacts.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Total Earnings */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-rodeo-500">
          <div className="flex items-center">
            <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-rodeo-600 mr-2 sm:mr-3" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-kci-600">Total Earnings</p>
              <p className="text-lg sm:text-2xl font-bold text-kci-900">
                ${analyticsData.financial.totalEarnings.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Total Expenses */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-kci-500">
          <div className="flex items-center">
            <DollarSign className="h-6 w-6 sm:h-6 sm:w-8 text-kci-600 mr-2 sm:mr-3" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-kci-600">Total Expenses</p>
              <p className="text-lg sm:text-2xl font-bold text-kci-900">
                ${analyticsData.financial.totalExpenses.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Net Profit/Loss */}
        <div className={`bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 ${
          analyticsData.financial.netProfit >= 0 ? 'border-rodeo-500' : 'border-kci-500'
        }`}>
          <div className="flex items-center">
            <TrendingUp className={`h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 ${
              analyticsData.financial.netProfit >= 0 ? 'text-rodeo-600' : 'text-kci-600'
            }`} />
            <div>
              <p className="text-xs sm:text-sm font-medium text-kci-600">Net Profit/Loss</p>
              <p className={`text-lg sm:text-2xl font-bold ${
                analyticsData.financial.netProfit >= 0 ? 'text-rodeo-600' : 'text-kci-600'
              }`}>
                ${analyticsData.financial.netProfit.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Total Points */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-earth-500">
          <div className="flex items-center">
            <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-earth-600 mr-2 sm:mr-3" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-kci-600">Total Points</p>
              <p className="text-lg sm:text-2xl font-bold text-kci-900">
                {analyticsData.performance.totalPoints.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        {/* Financial Breakdown */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-black-500 mb-3 sm:mb-4 flex items-center">
            <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-black-500 mr-2" />
            Financial Breakdown
          </h3>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-kci-100">
              <span className="text-xs sm:text-sm text-kci-600">Entries Cost</span>
              <span className="text-xs sm:text-sm font-medium">${analyticsData.financial.competitionCosts.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-kci-100">
              <span className="text-xs sm:text-sm text-kci-600">Membership Costs</span>
              <span className="text-xs sm:text-sm font-medium">${analyticsData.financial.membershipCosts.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-kci-100">
              <span className="text-xs sm:text-sm text-kci-600">Supplies Costs</span>
              <span className="text-xs sm:text-sm font-medium">${(analyticsData.financial.suppliesCosts || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-kci-100">
              <span className="text-xs sm:text-sm text-kci-600">Horse Care Costs</span>
              <span className="text-xs sm:text-sm font-medium">${analyticsData.financial.horseCareCosts.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 pt-2">
              <span className="text-xs sm:text-sm font-semibold text-kci-900">Total Expenses</span>
              <span className="text-xs sm:text-sm font-bold text-kci-900">${analyticsData.financial.totalExpenses.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Performance Metrics - Only show for non-basic subscriptions */}
        {!isBasicSubscription() && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-black-500 mb-3 sm:mb-4 flex items-center">
              <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-black-500 mr-2" />
              Performance Metrics
            </h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-kci-100">
                <span className="text-xs sm:text-sm text-kci-600">Total Results (All Time)</span>
                <span className="text-xs sm:text-sm font-medium">{analyticsData.performance.totalResults}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-kci-100">
                <span className="text-xs sm:text-sm text-kci-600">Results This Year</span>
                <span className="text-xs sm:text-sm font-medium">{analyticsData.performance.totalShows}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-kci-100">
                <span className="text-xs sm:text-sm text-kci-600">Average Points per Result</span>
                <span className="text-xs sm:text-sm font-medium">{analyticsData.performance.averagePointsPerShow.toFixed(1)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-kci-100">
                <span className="text-xs sm:text-sm text-kci-600">Total Horses</span>
                <span className="text-xs sm:text-sm font-medium">{analyticsData.horses.totalHorses}</span>
              </div>
              <div className="flex justify-between items-center py-2 pt-2">
                <span className="text-xs sm:text-sm text-kci-600">Average Cost per Horse</span>
                <span className="text-xs sm:text-sm font-medium">${analyticsData.horses.averageCostPerHorse.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* No Data Message for Family Members */}
      {selectedContactId !== 'all' && selectedContactId !== 'current_user' && 
       analyticsData.financial.totalEarnings === 0 && analyticsData.financial.totalExpenses === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                No data found for this family member
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  This family member doesn't have any results, entries, or memberships yet. 
                  To see data for this family member, try importing new data with this family member selected.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Performing Horses - Only show for non-basic subscriptions */}
      {!isBasicSubscription() && analyticsData.performance?.topPerformingHorses?.length > 0 && (
        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow p-4 sm:p-6 mb-6 sm:mb-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-black-500 mr-2" />
            Top Performing Horses
          </h3>
          
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3">
            {analyticsData.performance?.topPerformingHorses?.map((horse, index) => (
              <div key={horse.id} className={`bg-gray-50 rounded-lg p-3 ${index === 0 ? 'border-2 border-yellow-300' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {index === 0 && <Trophy className="h-4 w-4 text-black-500 mr-2" />}
                    <span className="font-medium text-gray-900">{horse.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    horse.netProfit >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    ${horse.netProfit.toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>Earnings: <span className="font-medium">${horse.earnings.toLocaleString()}</span></div>
                  <div>Points: <span className="font-medium">{horse.points.toLocaleString()}</span></div>
                  <div>Care Costs: <span className="font-medium">${horse.careCosts.toLocaleString()}</span></div>
                  <div>Results: <span className="font-medium">{horse.shows}</span></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop: Table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horse</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Care Costs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Profit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"># of Results</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyticsData.performance?.topPerformingHorses?.map((horse, index) => (
                  <tr key={horse.id} className={index === 0 ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {index === 0 && <Trophy className="h-4 w-4 text-yellow-600 mr-2" />}
                        <span className="text-sm font-medium text-gray-900">{horse.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.earnings.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {horse.points.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.careCosts.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                      horse.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${horse.netProfit.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {horse.shows}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Performing Shows - Only show for non-basic subscriptions */}
      {!isBasicSubscription() && analyticsData.performance?.topPerformingShows?.length > 0 && (
        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow p-4 sm:p-6 mb-6 sm:mb-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-black-500 mr-2" />
            Top Performing Shows
          </h3>
          
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3">
            {analyticsData.performance?.topPerformingShows?.map((show, index) => (
              <div key={show.name} className={`bg-gray-50 rounded-lg p-3 ${index === 0 ? 'border-2 border-purple-300' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {index === 0 && <Calendar className="h-4 w-4 text-black-500 mr-2" />}
                    <span className="font-medium text-gray-900">{show.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    show.netProfit >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    ${show.netProfit.toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>Date: <span className="font-medium">{formatDate(show.date)}</span></div>
                  <div>Earnings: <span className="font-medium">${show.totalEarnings.toLocaleString()}</span></div>
                  <div>Points: <span className="font-medium">{show.totalPoints.toLocaleString()}</span></div>
                  <div>Horses: <span className="font-medium">{show.horseCount}</span></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop: Table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Show</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horses</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Profit</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyticsData.performance?.topPerformingShows?.map((show, index) => (
                  <tr key={show.name} className={index === 0 ? 'bg-purple-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {index === 0 && <Calendar className="h-4 w-4 text-black-600 mr-2" />}
                        <span className="text-sm font-medium text-gray-900">{show.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(show.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${show.totalEarnings.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {show.totalPoints.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {show.horseCount}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                      show.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${show.netProfit.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Horse Cost Breakdown */}
      {analyticsData.horses?.horseBreakdown?.length > 0 && (
        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow p-4 sm:p-6 mb-6 sm:mb-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <Icon iconNode={horseHead} className="h-4 w-4 sm:h-5 sm:w-5 text-black-500 mr-2" />
            Horse Cost Breakdown
          </h3>
          
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3">
            {analyticsData.horses?.horseBreakdown?.map((horse) => (
              <div key={horse.name} className="bg-gray-50 rounded-lg p-3">
                <div className="font-medium text-gray-900 mb-2">{horse.name}</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>Farrier: <span className="font-medium">${horse.breakdown.farrier.toLocaleString()}</span></div>
                  <div>Dental: <span className="font-medium">${horse.breakdown.dental.toLocaleString()}</span></div>
                  <div>Deworming: <span className="font-medium">${horse.breakdown.deworming.toLocaleString()}</span></div>
                  <div>Medical: <span className="font-medium">${horse.breakdown.medical.toLocaleString()}</span></div>
                  <div>Breeding: <span className="font-medium">${horse.breakdown.breeding.toLocaleString()}</span></div>
                  <div className="col-span-2 font-semibold text-gray-900 border-t pt-1">
                    Total: ${horse.totalCost.toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Grand Total for Mobile */}
            <div className="bg-kci-100 rounded-lg p-3 mt-3">
              <div className="font-bold text-lg text-gray-900 text-center">
                Grand Total: ${analyticsData.horses.grandTotalHorseCosts.toLocaleString()}
              </div>
            </div>
          </div>
          
          {/* Desktop: Table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horse</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farrier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dental</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deworming</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medical</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Breeding</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyticsData.horses?.horseBreakdown?.map((horse) => (
                  <tr key={horse.name}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {horse.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.breakdown.farrier.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.breakdown.dental.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.breakdown.deworming.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.breakdown.medical.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${horse.breakdown.breeding.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ${horse.totalCost.toLocaleString()}
                    </td>
                  </tr>
                ))}
                
                {/* Grand Total Row */}
                <tr className="bg-kci-100 font-bold">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    GRAND TOTAL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${Object.values(analyticsData.horses.horseBreakdown).reduce((sum, horse) => sum + horse.breakdown.farrier, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${Object.values(analyticsData.horses.horseBreakdown).reduce((sum, horse) => sum + horse.breakdown.dental, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${Object.values(analyticsData.horses.horseBreakdown).reduce((sum, horse) => sum + horse.breakdown.deworming, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${Object.values(analyticsData.horses.horseBreakdown).reduce((sum, horse) => sum + horse.breakdown.medical, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${Object.values(analyticsData.horses.horseBreakdown).reduce((sum, horse) => sum + horse.breakdown.breeding, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${analyticsData.horses.grandTotalHorseCosts.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Coming Soon Section */}
      <div className="mt-6 sm:mt-8 bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
        <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Coming Soon</h3>
        <p className="text-blue-700 mb-3 sm:mb-4 text-sm sm:text-base">
          We're working on enhanced analytics features including:
        </p>
        <ul className="text-blue-700 space-y-1 text-xs sm:text-sm">
          <li>• Monthly trend charts and graphs</li>
          <li>• Individual horse performance analysis</li>
          <li>• Show-by-show profitability breakdown</li>
          <li>• Export capabilities for reports</li>
          <li>• Budget tracking and goal setting</li>
        </ul>
      </div>
    </div>
  );
}
